import { authGet, authPost } from './apiService';

/** Client wait for POST /api/pull-requests; keep ≥ server ADD_PULL_REQUEST_HTTP_TIMEOUT_MS (+ socket buffer). */
const ADD_PULL_REQUEST_CLIENT_TIMEOUT_MS =
  Number(process.env.REACT_APP_ADD_PULL_REQUEST_CLIENT_TIMEOUT_MS) || 45 * 60 * 1000;

/**
 * Pull Request service for API interactions
 */
export const pullRequestService = {
  /**
   * Add or update a PR and run Drill4J impacted-tests analysis (long-running).
   * @param {{ prNumber: number, appId: string }} body
   */
  async addOrUpdatePullRequest(body) {
    return authPost('/api/pull-requests', body, { timeout: ADD_PULL_REQUEST_CLIENT_TIMEOUT_MS });
  },

  /**
   * Sync pull requests for a repository
   * @param {string} repositoryId - Repository ID
   * @param {boolean} force - Force full sync
   * @returns {Promise<Object>} Sync result
   */
  async syncPullRequests(repositoryId, force = false) {
    try {
      return await authPost(`/api/pull-requests/sync/${repositoryId}`, { force });
    } catch (error) {
      console.error('Error syncing pull requests:', error);
      throw error;
    }
  },

  /**
   * Get pull requests by repository
   * @param {string} repositoryId - Repository ID
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Pull requests data
   */
  async getPullRequestsByRepository(repositoryId, filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.state) queryParams.append('state', filters.state);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

      const queryString = queryParams.toString();
      const url = `/api/pull-requests/repository/${repositoryId}${queryString ? `?${queryString}` : ''}`;
      
      return await authGet(url);
    } catch (error) {
      console.error('Error fetching pull requests:', error);
      throw error;
    }
  },

  /**
   * Get pull request statistics for a repository
   * @param {string} repositoryId - Repository ID
   * @returns {Promise<Object>} Statistics data
   */
  async getPullRequestStats(repositoryId) {
    try {
      return await authGet(`/api/pull-requests/stats/${repositoryId}`);
    } catch (error) {
      console.error('Error fetching PR statistics:', error);
      throw error;
    }
  },

  /**
   * Get repositories that have PR data
   * @returns {Promise<Array>} Array of repositories with PR data
   */
  async getRepositoriesWithPRs() {
    try {
      return await authGet('/api/pull-requests/repositories');
    } catch (error) {
      console.error('Error fetching repositories with PRs:', error);
      throw error;
    }
  }
};
